<script setup lang="ts">
/**
 * Haut des pages en charte « apple » (Karim 2026-10-18, modèle /cagnotte) :
 * fil d'Ariane, pastille facultative, très grand titre centré (fin du titre
 * en bleu Donoor), chapô, bouton pilule d'inscription + lien facultatif, puis
 * le visuel de la page (slot `visuel`). Entrée en CSS pur, dès le premier
 * rendu (rien n'attend l'hydratation).
 */
import type { CibleLien } from "~/types/cibleLien";
import { TITRE_APPLE } from "~/utils/titresApple";

const props = defineProps<{
  /** Libellé « Accueil » (traduit par la page). */
  accueil: string;
  /** Page courante dans le fil d'Ariane. */
  fil: string;
  badge?: string;
  badgeIcone?: string;
  titre: string;
  /** Fin du titre, en bleu (facultatif). */
  titreAccent?: string;
  description: string;
  cta: string;
  /** Source de la mesure des clics (directive v-register-cta). */
  ctaSource: string;
  /** Destination du bouton (défaut : inscription avec attribution). */
  ctaTo?: CibleLien;
  lienTexte?: string;
  lienTo?: CibleLien;
}>();

const localePath = useLocalePath();
const registerLink = useRegisterLink();
</script>

<template>
  <section class="overflow-hidden px-4 pt-32 pb-24 sm:px-8 lg:pt-40 lg:pb-32">
    <div class="mx-auto max-w-[920px] text-center">
      <nav :aria-label="props.accueil" class="entree mb-6 flex items-center justify-center gap-1.5 text-[13px] text-[#6e6e73]">
        <NuxtLink :to="localePath({ path: '/' })" class="hover:text-[#1d1d1f]">{{ props.accueil }}</NuxtLink>
        <UIcon name="i-lucide-chevron-right" class="size-3.5" />
        <span class="text-[#1d1d1f]">{{ props.fil }}</span>
      </nav>
      <p
        v-if="props.badge"
        class="entree mb-5 inline-flex items-center gap-1.5 rounded-full bg-[#f5f5f7] px-3.5 py-1.5 text-[13px] font-semibold text-(--color-main)"
        style="--d: 40ms"
      >
        <UIcon :name="props.badgeIcone ?? 'i-lucide-sparkles'" class="size-4" />
        {{ props.badge }}
      </p>
      <h1
        class="entree mx-auto mb-6 text-[40px] text-[#1d1d1f] [text-wrap:balance] sm:text-[60px] lg:text-[76px]"
        style="--d: 80ms"
        :style="TITRE_APPLE"
      >
        {{ props.titre }}<template v-if="props.titreAccent">{{ " " }}<span class="text-(--color-main)">{{ props.titreAccent }}</span></template>
      </h1>
      <p class="entree mx-auto mb-9 max-w-[700px] text-[19px] font-medium leading-[1.45] text-[#424245] sm:text-[21px]" style="--d: 160ms">
        {{ props.description }}
      </p>
      <div class="entree flex flex-wrap items-center justify-center gap-x-8 gap-y-4" style="--d: 240ms">
        <BoutonApple v-register-cta="props.ctaSource" :to="props.ctaTo ?? registerLink" grand>
          {{ props.cta }}
        </BoutonApple>
        <LienApple v-if="props.lienTexte && props.lienTo" :to="props.lienTo">{{ props.lienTexte }}</LienApple>
      </div>
    </div>
    <div v-if="$slots.visuel" class="entree mt-16 lg:mt-20" style="--d: 320ms">
      <slot name="visuel" />
    </div>
  </section>
</template>

<style scoped>
.entree {
  animation: entree 900ms cubic-bezier(0.28, 0, 0.22, 1) both;
  animation-delay: var(--d, 0ms);
}
@keyframes entree {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .entree {
    animation: none;
  }
}
</style>
